import type { ItemCategory, ItemDefinition } from "../types";

export type SrdRuleset = "2014" | "2024";
export type SrdResourceType = "magic-items" | "equipment";

export interface SrdSearchResult {
  index: string;
  name: string;
  url: string;
  resourceType: SrdResourceType;
}

export interface SrdItemPreview {
  index: string;
  name: string;
  resourceType: SrdResourceType;
  ruleset: SrdRuleset;
  category: ItemCategory;
  subtype?: string;
  rarity?: string;
  magical: boolean;
  requiresAttunement: boolean;
  attunementRequirements?: string;
  consumable: boolean;
  charges?: number;
  weight?: number;
  valueGp?: number;
  summary: string;
  mechanics: string;
  imageUrl?: string;
  sourceUrl: string;
  raw: Record<string, unknown>;
}

const HOST = "https://www.dnd5eapi.co";

async function getJson(url: string) {
  const response = await fetch(url, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`SRD API returned ${response.status}`);
  return response.json();
}

function absoluteUrl(url?: string) {
  if (!url) return undefined;
  return url.startsWith("http") ? url : `${HOST}${url}`;
}

function categoryFrom(name: string, resourceType: SrdResourceType, apiCategory?: string): ItemCategory {
  const n = `${name} ${apiCategory || ""}`.toLowerCase();
  if (n.includes("potion")) return "potion";
  if (n.includes("scroll")) return "scroll";
  if (n.includes("ring")) return "ring";
  if (n.includes("rod")) return "rod";
  if (n.includes("staff")) return "staff";
  if (n.includes("wand")) return "wand";
  if (n.includes("armor") || n.includes("shield")) return "armor";
  if (n.includes("weapon") || n.includes("sword") || n.includes("bow") || n.includes("axe") || n.includes("mace") || n.includes("dagger")) return "weapon";
  if (resourceType === "equipment") {
    if (n.includes("tool")) return "tool";
    if (n.includes("gear") || n.includes("adventuring")) return "mundane";
  }
  return "wondrous_item";
}

function textArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : value ? [String(value)] : [];
}

function parseAttunement(text: string) {
  const lower = text.toLowerCase();
  const requires = lower.includes("requires attunement") || lower.includes("attunement");
  if (!requires) return { requiresAttunement: false as const, attunementRequirements: undefined };
  const match = text.match(/requires attunement(?: by ([^)]+))?/i);
  return {
    requiresAttunement: true as const,
    attunementRequirements: match?.[1]?.trim()
  };
}

function parseCharges(text: string) {
  const match = text.match(/(\d+)\s+charges?/i);
  return match ? Number(match[1]) : undefined;
}

function parseCostGp(cost: any): number | undefined {
  if (!cost || typeof cost.quantity !== "number") return undefined;
  const unit = String(cost.unit || "").toLowerCase();
  if (unit === "gp") return cost.quantity;
  if (unit === "sp") return cost.quantity / 10;
  if (unit === "cp") return cost.quantity / 100;
  if (unit === "ep") return cost.quantity / 2;
  if (unit === "pp") return cost.quantity * 10;
  return undefined;
}

export async function searchSrdItems(
  query: string,
  ruleset: SrdRuleset,
  resourceType: SrdResourceType
): Promise<SrdSearchResult[]> {
  const data = await getJson(`${HOST}/api/${ruleset}/${resourceType}`);
  const q = query.trim().toLowerCase();
  const results = Array.isArray(data.results) ? data.results : [];
  return results
    .filter((x: any) => !q || String(x.name || "").toLowerCase().includes(q))
    .slice(0, 50)
    .map((x: any) => ({
      index: String(x.index),
      name: String(x.name),
      url: absoluteUrl(x.url) || `${HOST}/api/${ruleset}/${resourceType}/${x.index}`,
      resourceType
    }));
}

export async function getSrdItem(
  index: string,
  ruleset: SrdRuleset,
  resourceType: SrdResourceType
): Promise<SrdItemPreview> {
  const sourceUrl = `${HOST}/api/${ruleset}/${resourceType}/${index}`;
  const d: any = await getJson(sourceUrl);
  const descParts = textArray(d.desc);
  const desc = descParts.join("\n\n");
  const apiCategory = d.equipment_category?.name || d.gear_category?.name || d.category_range || "";
  const attune = parseAttunement(desc);
  const category = categoryFrom(String(d.name || index), resourceType, apiCategory);
  const consumable = category === "potion" || category === "scroll";
  const summary = descParts[0] || String(d.name || index);
  const subtype =
    d.gear_category?.name ||
    d.weapon_category ||
    d.armor_category ||
    d.equipment_category?.name ||
    undefined;

  return {
    index: String(d.index || index),
    name: String(d.name || index),
    resourceType,
    ruleset,
    category,
    subtype,
    rarity: d.rarity?.name,
    magical: resourceType === "magic-items",
    requiresAttunement: attune.requiresAttunement,
    attunementRequirements: attune.attunementRequirements,
    consumable,
    charges: parseCharges(desc),
    weight: typeof d.weight === "number" ? d.weight : undefined,
    valueGp: parseCostGp(d.cost),
    summary,
    mechanics: desc,
    imageUrl: absoluteUrl(d.image),
    sourceUrl,
    raw: d
  };
}

export function previewToDefinition(preview: SrdItemPreview, id: string, timestamp: string): ItemDefinition {
  return {
    id,
    workspaceScope: "local",
    name: preview.name,
    category: preview.category,
    subtype: preview.subtype,
    rarity: preview.rarity,
    magical: preview.magical,
    requiresAttunement: preview.requiresAttunement,
    attunementRequirements: preview.attunementRequirements,
    consumable: preview.consumable,
    charges: preview.charges,
    weight: preview.weight,
    valueGp: preview.valueGp,
    source: `D&D ${preview.ruleset} SRD`,
    rulesVersion: preview.ruleset === "2024" ? "D&D 2024 / SRD 5.2" : "D&D 2014 / SRD 5.1",
    summary: preview.summary,
    mechanics: preview.mechanics,
    lore: "",
    gmSecrets: "",
    imageUrl: preview.imageUrl,
    tags: ["SRD", preview.ruleset, preview.resourceType === "magic-items" ? "magic item" : "equipment"],
    sourceType: "srd_api",
    sourceRuleset: preview.ruleset,
    sourceResourceType: preview.resourceType,
    sourceExternalId: preview.index,
    sourceUrl: preview.sourceUrl,
    importedAt: timestamp,
    createdAt: timestamp,
    updatedAt: timestamp
  };
}
